import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { JobsContext } from 'libs.jobs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
/**
 * Hooks for the backend-owned study translation orchestration endpoints in
 * `translation_v2` (SSP-1114/1116). Each endpoint performs the multi-step write
 * (asset init / l10n map publish / study config update) with best-effort rollback
 * on the backend, so the browser no longer orchestrates those steps itself.
 *
 * Reached through the WEB proxy at `/api/v2/translation/*`; the Firebase bearer
 * token carries the entity/medable claims the backend needs.
 */
const STUDY_ENDPOINT = '/api/v2/translation/study';
function useStudyOrchestrationPost() {
    const { post } = useApiClient(ApiClientService.WEB);
    const { firebaseToken } = useContext(FirebaseAuthContext);
    return (path, body) => post(`${STUDY_ENDPOINT}${path}`, body, {
        withCredentials: false,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`,
        },
    });
}
/**
 * Enable/disable study translations. Enable returns a `jobId` and runs asynchronously —
 * `onStatusChanged` fires when the job completes. Disable is synchronous: the endpoint
 * returns `{ localizationStatus, l10nMapResourceId }` directly and `onStatusChanged`
 * fires immediately. `onStatusError` fires on failure of either path, so the caller can
 * drive a progress affordance and refresh (or revert) its in-memory study.
 */
export function useSetStudyStatus({ onStatusChanged, onStatusError, } = {}) {
    const post = useStudyOrchestrationPost();
    const jobsContext = useContext(JobsContext);
    return useMutation({
        mutationKey: ['study', 'translations', 'status'],
        mutationFn: (body) => post('/status', body),
        onSuccess: (response) => {
            const data = response?.data;
            // Async path (enable): track the returned job to completion.
            if (data?.jobId && jobsContext) {
                jobsContext.addJobHandler(data.jobId, {
                    onDone: (message) => onStatusChanged?.({
                        localizationStatus: message?.params?.localizationStatus,
                        l10nMapResourceId: message?.params?.l10nMapResourceId,
                    }),
                    onError: (message) => onStatusError?.(message?.message_key),
                });
                return;
            }
            // Synchronous path (disable): the endpoint already ran the orchestration.
            if (data?.localizationStatus) {
                onStatusChanged?.({
                    localizationStatus: data.localizationStatus,
                    l10nMapResourceId: data.l10nMapResourceId,
                });
                return;
            }
            onStatusError?.();
        },
        onError: () => onStatusError?.(),
        retry: false,
    });
}
export function useSetStudyAssetVersion() {
    const post = useStudyOrchestrationPost();
    return useMutation({
        mutationKey: ['study', 'translations', 'version'],
        mutationFn: (body) => post('/asset_version', body),
        retry: false,
    });
}
export function useSetAssetStatus() {
    const post = useStudyOrchestrationPost();
    return useMutation({
        mutationKey: ['study', 'translations', 'asset_status'],
        mutationFn: (body) => post('/asset_status', body),
        retry: false,
    });
}
